import { connect } from 'react-redux';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { max } from 'date-fns';
import spacetime from 'spacetime';
import { v4 as uuidv4 } from 'uuid';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmErknmPositionsSelectOptions,
  getKnmFormRef,
  getKnmOnSaveButtonClick,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldMark } from 'KnmPackage/MainModule/components/FieldMark/FieldMark';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import {
  getDecisionFormRef,
  getDecisionOnSaveButtonClick,
  getDecisionPositionsSelectOptions,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { agreementsChangeTypeSelectOptions } from 'KnmPackage/MainModule/constants/agreementsChangeTypeSelectOptions';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { Row } from 'UIPackage/FormModule/components/Row';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IErknmFormAgreementChange } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreementChange';
import { getAgreementChangeValidate } from 'KnmErknmPackage/MainModule/common/getAgreementChangeValidate';
import { geCatalogsAgreementChangeReasonSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IAgreementChangeReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IAgreementChangeReasonSelectOption';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import styles from './ChangeAgreementModal.module.scss';
import { AttachmentsInModals } from '../../AttachmentsInModals/AttachmentsInModals';

interface IChangeAgreementModalProps {
  visible: boolean;
  onClose: () => void;
  onCloseAll: () => void;
  name: string;
}

interface IChangeAgreementModalStoreProps {
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  decisionPositionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  onSaveButtonClickKnmAndPm: (() => void) | undefined;
  onSaveButtonClickDecision: (() => void) | undefined;
  formRefKnm: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
  agreementChangeReasons: IAgreementChangeReasonSelectOption[];
}

interface IForm extends IErknmFormAgreementChange {}

const ChangeAgreementModalWithoutStore = ({
  name,
  visible,
  onClose,
  onCloseAll,
  formRefKnm,
  decisionFormRef,
  agreementChangeReasons,
  positionsSelectOptions,
  onSaveButtonClickDecision,
  onSaveButtonClickKnmAndPm,
  decisionPositionsSelectOptions,
}: IChangeAgreementModalProps & IChangeAgreementModalStoreProps) => {
  const formRef = useRef<FormApi<IForm, Partial<IForm>>>();
  const { isDecision, agreement } = useContext(AgreementsContext);

  useEffect(() => {
    if (visible) {
      formRef.current?.change('types', [{ guid: uuidv4(), type: undefined }]);
      // @ts-ignore
      formRef.current?.change('reasons', [undefined]);
    }
  }, [visible]);

  const handleClose = () => {
    onClose();
    formRef?.current?.reset();
  };

  const handleSave = useCallback(
    async (values: IForm) => {
      const newChanges: IErknmFormAgreementChange[] = agreement?.changes || [];

      newChanges.push({
        ...values,
        isNew: true,
        signed: false,
        guid: uuidv4(),
      });

      if (isDecision) {
        decisionFormRef?.current.change(`${name}.signed` as keyof IDecisionForm, false);
        // @ts-ignore
        decisionFormRef?.current.change(`${name}.changes` as keyof IDecisionForm, newChanges);
        onSaveButtonClickDecision && onSaveButtonClickDecision();
      } else {
        formRefKnm?.current.change(`${name}.signed` as keyof IKnmForm, false);
        // @ts-ignore
        formRefKnm?.current.change(`${name}.changes` as keyof IKnmForm, newChanges);
        onSaveButtonClickKnmAndPm && onSaveButtonClickKnmAndPm();
      }
      onCloseAll();
      formRef?.current?.reset();
    },
    [onCloseAll, formRef, agreement],
  );

  const addType = useCallback(() => {
    formRef.current?.mutators.push('types', { guid: uuidv4(), type: undefined });
  }, [formRef]);

  const addReason = useCallback(() => {
    formRef.current?.mutators.push('reasons', undefined);
  }, [formRef]);

  const renderContentForm = useCallback(
    ({ handleSubmit, form, values, errors, touched }: FormRenderProps<IForm>) => {
      formRef.current = form;

      const unusedAgreementsChangeTypeSelectOptions = agreementsChangeTypeSelectOptions.filter(
        type => !values.types?.find(i => i?.type?.id === type.id),
      );

      const unusedAgreementReasonsChangeSelectOptions = agreementChangeReasons
        .filter(el => values.types?.map(i => i?.type?.value).includes(el.code))
        .filter(reason => !values.reasons?.find(i => i?.id === reason.id));

      const minTermOfExecutionAgreement = () => {
        const termOfExecutionAgreement =
          parseDate({
            date: agreement?.termOfExecutionAgreement || '',
            parseFormat: DATE_FORMAT_LONG_FNS,
          }) || new Date();

        if (!agreement?.changes) return max([new Date(), termOfExecutionAgreement]);

        const otherTermOfExecutionAgreements = agreement.changes.map(i =>
          parseDate({
            date: i.termOfExecutionAgreement || '',
            parseFormat: DATE_FORMAT_LONG_FNS,
          }),
        ) as Date[];

        return max(otherTermOfExecutionAgreements);
      };

      return (
        <>
          <ModalBody size="content" padding="small">
            <div className={styles.ContentWrapper}>
              <FieldBlock
                label={<FieldMark fieldName="types" label="Вид изменения" />}
                button={
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    onClick={addType}
                  />
                }
              >
                <div className={styles.ReasonsWrapper}>
                  <FieldArray name="types" validate={makeRequired(REQUIRED_ERROR)}>
                    {({ fields }: FieldArrayRenderProps<ISelectOption<string | number>, HTMLElement>) => {
                      if (!fields.length) {
                        return null;
                      }

                      return fields.map((terminationReasonsName, index) => {
                        const fieldValue: ISelectOption<string | number> | undefined = fields.value?.[index];

                        return (
                          <Row key={`types.${index}.${fieldValue?.id}`} margin="none" isListItem>
                            <div className={`shared-field-row ${styles.TerminationReasonsRow}`}>
                              <SelectInputField
                                name={`${terminationReasonsName}.type`}
                                placeholder={'Вид'}
                                size="medium"
                                options={unusedAgreementsChangeTypeSelectOptions}
                              />
                              <CloseButton
                                className="shared-deleting-row-btn"
                                onClick={() => formRef.current?.mutators.remove('types', index)}
                              />
                            </div>
                          </Row>
                        );
                      });
                    }}
                  </FieldArray>
                </div>
                {errors?.types?.['FINAL_FORM/array-error'] && touched?.types && (
                  <p className="shared-input-error">Поле не может быть пустым</p>
                )}
              </FieldBlock>

              <FieldBlock
                margin="top"
                label={<FieldMark fieldName="reasons" label="Основание для изменения" />}
                button={
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    onClick={addReason}
                  />
                }
              >
                <div className={styles.ReasonsWrapper}>
                  <FieldArray name="reasons" validate={makeRequired(REQUIRED_ERROR)}>
                    {({ fields }: FieldArrayRenderProps<IAgreementChangeReasonSelectOption, HTMLElement>) => {
                      if (!fields.length) {
                        return null;
                      }

                      return fields.map((reasonsName, index) => {
                        const fieldValue: IAgreementChangeReasonSelectOption | undefined = fields.value?.[index];

                        return (
                          <Row key={`reasons.${index}.${fieldValue?.id}`} margin="none" isListItem>
                            <div className={`shared-field-row ${styles.TerminationReasonsRow}`}>
                              <SelectInputField
                                name={reasonsName}
                                placeholder={'Основание'}
                                size="medium"
                                options={unusedAgreementReasonsChangeSelectOptions}
                              />
                              <CloseButton
                                className="shared-deleting-row-btn"
                                onClick={() => formRef.current?.mutators.remove('reasons', index)}
                              />
                            </div>
                          </Row>
                        );
                      });
                    }}
                  </FieldArray>
                </div>
                {errors?.reasons?.['FINAL_FORM/array-error'] && touched?.reasons && (
                  <p className="shared-input-error">Поле не может быть пустым</p>
                )}
              </FieldBlock>

              <AttachmentsInModals
                formRef={formRef}
                formats=".pdf"
                name="attachment"
                title="Ходатайство об изменении соглашения"
                type="one"
                hideButtonAdd={!!values.attachment?.length}
              />

              {values.types?.find(type => type.type?.value === 'EXTENSION_OF_DEADLINE') && (
                <FieldBlock
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  label={<FieldMark fieldName="termOfExecutionAgreement" label="Новый срок исполнения" />}
                >
                  <div>
                    <DatePickerField
                      name={`termOfExecutionAgreement`}
                      placeholder={'Новый срок исполнения'}
                      size="medium"
                      className={styles.DatePicker}
                      minDate={minTermOfExecutionAgreement()}
                      maxDate={spacetime().add(3, 'years').toNativeDate()}
                    />
                  </div>
                </FieldBlock>
              )}

              <AttachmentsInModals
                formRef={formRef}
                formats=".pdf"
                name="updatedAgreementAttachment"
                title="Обновленная версия соглашения"
                type="one"
                hideButtonAdd={!!values.updatedAgreementAttachment?.length}
              />

              <AttachmentsInModals
                formRef={formRef}
                formats=".pdf"
                name="petitionAttachment"
                title="Решение об изменении"
                type="one"
                hideButtonAdd={!!values.petitionAttachment?.length}
              />

              <FieldBlock label={'Решение принято'} margin="top">
                <FieldBlock
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  label={<FieldMark fieldName="fioSigner" label="ФИО принявшего решение об изменении" />}
                >
                  <TextInputField name={`fioSigner`} placeholder="ФИО" />
                </FieldBlock>
                <FieldBlock
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  label={<FieldMark fieldName="titleSigner" label="Должность принявшего решение об изменении" />}
                >
                  <SelectInputField
                    name={`titleSigner`}
                    placeholder="Выберите должность"
                    options={isDecision ? decisionPositionsSelectOptions : positionsSelectOptions}
                    isSearchable
                    size="medium"
                    opensOnTop
                  />
                </FieldBlock>
              </FieldBlock>
            </div>
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button text="Сохранить" color="primary" size="medium" type="button" onClick={handleSubmit} />
          </ModalActions>
        </>
      );
    },
    [positionsSelectOptions, handleClose],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader type="small" title="Изменение соглашения" />
      <Form<IForm>
        render={renderContentForm}
        onSubmit={handleSave}
        validate={getAgreementChangeValidate}
        mutators={{ ...arrayMutators }}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IChangeAgreementModalStoreProps => ({
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
  formRefKnm: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  onSaveButtonClickKnmAndPm: getKnmOnSaveButtonClick(state),
  onSaveButtonClickDecision: getDecisionOnSaveButtonClick(state),
  agreementChangeReasons: geCatalogsAgreementChangeReasonSelectOptions(state),
});

export const ChangeAgreementModal = connect(mapStateToProps)(ChangeAgreementModalWithoutStore);
